import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { type ApiError, api, type SignInResult, type VerifyMfaCodeResult } from '@whinerp/client';
import { Button, Field, FormMessage, Icon, SerifMoment, Skeleton } from '@whinerp/ui';
import { useT } from '../../app/i18n.js';
import { defineScreen } from '../types.js';

/**
 * SC-02 Sign in, with the second factor when the account has one.
 *
 * One decision per screen. Email and password are the default path, because
 * that is what somebody arrives expecting and it works whether or not email
 * sending is configured; the sign-in link is a secondary choice; and the
 * verification code is a separate step rather than a third field, because it
 * does not exist until the first two have been accepted.
 *
 * The rejection message never says whether the account exists. The deactivated
 * message does say so, on purpose: somebody whose account was switched off
 * needs to know that rather than doubting their own password for a week.
 */

/**
 * Where the provider sends people back to.
 *
 * The sign-in screen itself. It is registered by the API as the redirect_uri,
 * so it has to be the same string on both sides.
 */
const CALLBACK_URI = (): string => `${window.location.origin}/sign-in`;

type Step =
  | { kind: 'credentials' }
  | { kind: 'mfa'; challenge: string }
  | { kind: 'link-sent'; email: string };

function SignInScreen(): JSX.Element {
  const t = useT();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>({ kind: 'credentials' });
  /*
   * Where this person's organisation signs in, once we know there is one.
   *
   * Asked when they submit, not as they type: starting a sign-in writes an
   * in-flight request, and somebody tabbing in and out of the field should not
   * leave a trail of them. Shown as a button rather than followed
   * automatically - being thrown to a third-party site without touching
   * anything reads as a hijack, however correct it is.
   */
  const [provider, setProvider] = useState<string | null>(null);
  const { search } = useLocation();
  const [usePassword, setUsePassword] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [message, setMessage] = useState<string | null>(null);

  /*
   * The provider sends people back HERE, to the sign-in screen.
   *
   * There is no separate callback screen and there should not be: the screen
   * inventory has one place where signing in happens, and coming back from a
   * provider is still signing in. A blank page that exists only to exchange a
   * code is a screen nobody designed and everybody sees.
   */
  const callback = useMutation({
    mutationFn: (arrived: { state: string; code: string }) => api.ssoCallback({ body: arrived }),
    onSuccess: (result) => navigate(result.returnTo ?? '/'),
    onError: (error: ApiError) => setMessage(error.message),
  });

  const exchanged = useRef(false);
  useEffect(() => {
    // The router's location, not the browser's: this screen is mounted by the
    // router, and reading window directly works in a browser and silently sees
    // nothing anywhere else.
    const parameters = new URLSearchParams(search);
    const state = parameters.get('state');
    const code = parameters.get('code');
    if (!state || !code) return;
    // Once. A state is single-use, so a second exchange - from a re-render or
    // React's development double-effect - gets the replay refusal and shows the
    // person an error after a sign-in that actually worked.
    if (exchanged.current) return;
    exchanged.current = true;
    callback.mutate({ state, code });
    // Deliberately once, on mount: the query string does not change under us,
    // and the state in it can only be spent a single time.
  }, [callback, search]);

  const ssoStart = useMutation({
    mutationFn: () =>
      api.ssoStart({
        body: { email, redirectUri: CALLBACK_URI(), returnTo: null },
      }),
  });

  const signIn = useMutation<SignInResult, ApiError>({
    mutationFn: () => api.signIn({ body: { email, password } }),
    onSuccess: (result) => {
      setMessage(null);
      if (result.status === 'signed-in') navigate('/');
      else if (result.status === 'mfa-required') setStep({ kind: 'mfa', challenge: result.challenge });
      else setMessage(result.message);
    },
    onError: (error) => setMessage(error.message),
  });

  const requestLink = useMutation<unknown, ApiError>({
    mutationFn: () => api.requestSignInLink({ body: { email } }),
    onSuccess: () => setStep({ kind: 'link-sent', email }),
    onError: (error) => setMessage(error.message),
  });

  const verify = useMutation<VerifyMfaCodeResult, ApiError>({
    mutationFn: () =>
      api.verifyMfaCode({
        body: { challenge: step.kind === 'mfa' ? step.challenge : '', code },
      }),
    onSuccess: (result) => {
      if (result.status === 'signed-in') navigate('/');
      else {
        setMessage(result.message);
        // An expired challenge cannot be retried with another code, so the
        // person goes back rather than typing into a dead box.
        if (result.status === 'expired') setStep({ kind: 'credentials' });
      }
    },
    onError: (error) => setMessage(error.message),
  });

  return (
    <main className="flex min-h-screen items-center justify-center bg-canvas p-5">
      <div
        data-component="SignInCard"
        className="flex w-full max-w-modal-confirm flex-col gap-5 rounded-card bg-surface p-6"
      >
        <div className="flex flex-col gap-2">
          <Icon name="Diamond" decorative className="text-bronze" />
          <SerifMoment>
            {step.kind === 'mfa' ? t('auth.mfa.title') : t('auth.signin.title')}
          </SerifMoment>
          {step.kind === 'mfa' ? (
            <p className="text-body text-ink-meta">{t('auth.mfa.subtitle')}</p>
          ) : null}
        </div>

        {step.kind === 'link-sent' ? (
          <FormMessage tone="success">
            {t('auth.signin.linkSent', { values: { email: step.email } })}
          </FormMessage>
        ) : step.kind === 'mfa' ? (
          <form
            data-component="MfaForm"
            className="flex flex-col gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              setMessage(null);
              verify.mutate();
            }}
          >
            <Field
              label={t('auth.mfa.code')}
              value={code}
              onChange={(event) => setCode(event.target.value)}
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              required
              hint={t('auth.mfa.useRecovery')}
            />
            {message ? <FormMessage tone="error">{message}</FormMessage> : null}
            <Button type="submit" intent="primary" busy={verify.isPending}>
              {t('auth.mfa.submit')}
            </Button>
          </form>
        ) : (
          <form
            data-component="SignInForm"
            className="flex flex-col gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              setMessage(null);
              /*
               * The provider question first. A workspace that has enforced SSO
               * has turned the other ways off, so sending the password anyway
               * would earn a refusal that says nothing useful.
               */
              ssoStart.mutate(undefined, {
                onSuccess: (result) => {
                  if (result.status === 'redirect') {
                    setProvider(result.authorizeUrl);
                    return;
                  }
                  if (usePassword) signIn.mutate();
                  else requestLink.mutate();
                },
                onError: () => {
                  // The lookup is not the sign-in. If it cannot be reached, the
                  // password path is still there and still works.
                  if (usePassword) signIn.mutate();
                  else requestLink.mutate();
                },
              });
            }}
          >
            <Field
              label={t('auth.signin.email')}
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="email"
              required
            />

            {usePassword ? (
              <Field
                label={t('auth.signup.password')}
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                required
              />
            ) : null}

            {message ? <FormMessage tone="error">{message}</FormMessage> : null}

            {provider ? (
              <div className="flex flex-col gap-2">
                <p className="text-meta text-ink-meta">{t('auth.signin.sso.body')}</p>
                <a
                  data-sso-continue
                  href={provider}
                  className="inline-flex h-input items-center justify-center rounded-control border border-control bg-surface px-4 text-dense text-ink hover:bg-sunken"
                >
                  {t('auth.signin.sso')}
                </a>
              </div>
            ) : null}

            <Button
              type="submit"
              intent="primary"
              busy={signIn.isPending || requestLink.isPending || ssoStart.isPending}
            >
              {usePassword ? t('auth.signin.submit') : t('auth.signin.magicLink')}
            </Button>

            <button
              type="button"
              onClick={() => {
                setUsePassword((value) => !value);
                setMessage(null);
              }}
              className="self-start text-dense text-bronze-text underline decoration-bronze-rule underline-offset-4"
            >
              {usePassword ? t('auth.signin.useMagicLink') : t('auth.signin.usePassword')}
            </button>
          </form>
        )}

        <p className="text-meta text-ink-meta">
          <Link
            to="/sign-up"
            className="text-bronze-text underline decoration-bronze-rule underline-offset-4"
          >
            {t('action.signUp')}
          </Link>
        </p>
      </div>
    </main>
  );
}

function SignInLoading(): JSX.Element {
  return (
    <main
      className="flex min-h-screen items-center justify-center bg-canvas p-5"
      role="status"
      aria-live="polite"
      aria-busy
    >
      <span className="sr-only">Loading sign in</span>
      <div className="flex w-full max-w-modal-confirm flex-col gap-4 rounded-card bg-surface p-6">
        <Skeleton className="h-line-h2 w-block-md" />
        <Skeleton className="h-input w-full" />
        <Skeleton className="h-btn w-full" />
      </div>
    </main>
  );
}

export const screen = defineScreen({
  id: 'SC-02',
  route: '/sign-in',
  surface: 'page',
  title: 'Sign in',
  features: ['F-002', 'F-004', 'F-003'],
  entitlement: 'always-on',
  reachedBy: [{ kind: 'unauthenticated' }],
  components: [],
  elements: ['SignInCard', 'SignInForm'],
  states: {
    empty: { notApplicable: 'Sign in is a form, not a collection.' },
    loading: SignInLoading,
    error: {
      notApplicable:
        'A rejected sign-in is a message above the form, not a replacement for it: the email address stays typed.',
    },
  },
  component: SignInScreen,
});

export default screen;
